import React, { FC, Fragment, useState } from 'react';
import { Grid, TextField, Typography } from '@material-ui/core';
import { createUseStyles } from 'src/theming';
import Ticket, { TicketStates } from 'src/interfaces/api/Ticket';
import {
  CriticitySelect,
  DomainSelect,
  PrioritySelect,
  ProductTypeSelect,
  SupplierSelect,
  TicketTypeSelect,
} from 'src/components/ApiField';
import TicketStateSelect from './components/TicketStateSelect';
import SiteField from './components/SiteField';
import ContactField from './components/ContactField';
import DealField from './components/DealField';
import EmployeeField from './components/EmployeeField';
import UpdateTicketModal from './components/UpdateTicketModal';
import EditableTextField from './components/EditableTextField';
import SupportEmployeeField from './components/SupportEmployeeField';
import ExternalLinkAlert from './components/ExternalLinkButton';
import moment = require('moment');

interface FieldValue {
  fieldName: keyof Ticket;
  value: Ticket[keyof Ticket];
}

interface Props {
  ticket: Ticket;
  style?: Object;
  hidden?: boolean;
  onUpdateField: <T extends keyof Ticket>(fieldName: T, newValue: Ticket[T]) => void;
}

const useStyles = createUseStyles({
  root: {
    padding: 16,
  },
  title: {
    width: '100%',
    marginTop: 20,
  },
});

const DetailsCard: FC<Props> = ({ ticket, onUpdateField, ...props }) => {
  const [newFieldValue, setNewFieldValue] = useState<FieldValue>();
  const classes = useStyles();
  const isGlobal = !ticket.siteId;

  const handleValidateUpdate = async () => {
    await onUpdateField(newFieldValue!.fieldName, newFieldValue!.value);
    setNewFieldValue(undefined);
  };

  return (
    <div className={classes.root} {...props}>
      <Grid container spacing={3}>
        {ticket.stateId === TicketStates.attente_intervention_fournisseur && (
          <Grid item xs={12}>
            <ExternalLinkAlert ticketId={ticket.id} />
          </Grid>
        )}

        <Typography variant='overline' className={classes.title} color='primary'>
          Informations générales
        </Typography>

        {ticket.siteId && (
          <Grid item xs={12} md={ticket.dealId ? 6 : 12}>
            <SiteField siteId={ticket.siteId} />
          </Grid>
        )}

        {ticket.dealId && (
          <Grid item xs={12} md={6}>
            <DealField dealId={ticket.dealId} />
          </Grid>
        )}

        {ticket.closingDate ? (
          <Grid item xs={12} md={4}>
            <TextField
              variant='outlined'
              label='Clôturé le'
              defaultValue={moment(ticket.closingDate).format('DD-MM-YY à HH:mm')}
              InputProps={{ readOnly: true }}
              fullWidth
            />
          </Grid>
        ) : (
          <Grid item xs={12} md={4}>
            <TicketStateSelect
              onChange={(value) => setNewFieldValue({ fieldName: 'stateId', value })}
              value={ticket.stateId}
            />
          </Grid>
        )}

        <Grid item xs={6} md={4}>
          <CriticitySelect
            onChange={(event) =>
              setNewFieldValue({ fieldName: 'criticity', value: parseInt(event.target.value as string) })
            }
            value={ticket.criticity}
            fullWidth
          />
        </Grid>

        <Grid item xs={6} md={4}>
          <PrioritySelect
            onChange={(event) =>
              setNewFieldValue({ fieldName: 'priority', value: parseInt(event.target.value as string) })
            }
            value={ticket.priority}
            fullWidth
          />
        </Grid>

        {ticket.associatedContactId && (
          <Grid item xs={12} md={6}>
            <ContactField contactId={ticket.associatedContactId} />
          </Grid>
        )}

        {ticket.supportEmployeeId && (
          <Grid item xs={12} md={ticket.associatedContactId ? 6 : 12}>
            <SupportEmployeeField
              onChange={(value) => onUpdateField('supportEmployeeId', value)}
              employeeId={ticket.supportEmployeeId}
            />
          </Grid>
        )}

        {!isGlobal && (
          <Fragment>
            <Grid item xs={6}>
              <DomainSelect
                onChange={(event) => setNewFieldValue({ fieldName: 'domainId', value: event.target.value as string })}
                value={ticket.domainId}
                fullWidth
              />
            </Grid>

            <Grid item xs={6}>
              <TicketTypeSelect
                onChange={(event) => setNewFieldValue({ fieldName: 'type', value: event.target.value as string })}
                value={ticket.type}
              />
            </Grid>
          </Fragment>
        )}

        <Typography variant='overline' className={classes.title} color='primary'>
          Informations produit
        </Typography>

        <Grid item xs={ticket.productType ? 6 : 12}>
          <SupplierSelect
            onChange={(event) =>
              setNewFieldValue({ fieldName: 'supplierOfProductId', value: event.target.value as string })
            }
            value={ticket.supplierOfProductId}
            domainId={ticket.domainId}
          />
        </Grid>

        {ticket.productType && (
          <Grid item xs={6}>
            <ProductTypeSelect
              onChange={(event) => setNewFieldValue({ fieldName: 'productType', value: event.target.value as string })}
              domainId={ticket.domainId}
              value={ticket.productType}
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <EditableTextField
            label='URL ticket fournisseur'
            onSave={(value) => onUpdateField('urlSupplierTicket', value)}
            defaultValue={ticket.urlSupplierTicket}
          />
        </Grid>

        <Typography variant='overline' className={classes.title} color='primary'>
          Informations complémentaires
        </Typography>

        {ticket.creatorId && (
          <Grid item xs={12}>
            <EmployeeField label='Créateur' employeeId={ticket.creatorId} />
          </Grid>
        )}

        <Grid item xs={12}>
          <EditableTextField
            label='Commentaire'
            multiline
            onSave={(value) => onUpdateField('comment', value)}
            defaultValue={ticket.comment}
          />
        </Grid>
      </Grid>

      <UpdateTicketModal
        open={!!newFieldValue}
        onValidate={handleValidateUpdate}
        onClose={() => setNewFieldValue(undefined)}
      />
    </div>
  );
};

export default DetailsCard;
